import {
  BATCH_ASSIGN_TASKS_MAX,
  type BatchAssignTasksInput,
  type BatchAssignTasksResponse,
} from '@erp/rpc';
import type { SingleSelectOption } from 'src/components/common/SingleSelect';

/** Sentinel option id. User and agent ids are uuids, so this cannot collide. */
export const UNASSIGNED_REASSIGN_ID = '__unassigned__';

export type ReassignTarget =
  | { assigneeType: null }
  | { assigneeType: 'user'; assigneeUserId: string }
  | { assigneeType: 'agent'; assigneeAgentDefinitionId: string };

type ReassignGt = (
  message: string,
  options: {
    context: string;
    assigned?: number;
    skipped?: number;
  },
) => string;

function idAfter(optionId: string, prefix: string): string | null {
  if (!optionId.startsWith(prefix)) return null;
  const id = optionId.slice(prefix.length);
  return id.length > 0 ? id : null;
}

/** Map a picker option id onto the batch-assign body. Unknown ids are ignored. */
export function reassignTargetFromOptionId(
  optionId: string,
): ReassignTarget | null {
  if (optionId === UNASSIGNED_REASSIGN_ID) return { assigneeType: null };
  const userId = idAfter(optionId, 'user:');
  if (userId) return { assigneeType: 'user', assigneeUserId: userId };
  const agentId = idAfter(optionId, 'agent:');
  if (agentId) {
    return { assigneeType: 'agent', assigneeAgentDefinitionId: agentId };
  }
  return null;
}

/**
 * Picker options for mass-reassign. The current user is "Assign to me"
 * rather than a second copy of their name in the users group.
 */
export function buildReassignAssigneeOptions({
  gt,
  users,
  agents,
  currentUserId,
}: {
  gt: ReassignGt;
  users: ReadonlyArray<{ id: string; name: string }>;
  agents: ReadonlyArray<{ id: string; name: string }>;
  currentUserId: string | undefined;
}): SingleSelectOption[] {
  const usersGroup = gt('Users', {
    context: 'group label in the task mass-reassign picker',
  });
  const agentsGroup = gt('Agents', {
    context: 'group label in the task mass-reassign picker',
  });
  const me = currentUserId
    ? [
        {
          id: `user:${currentUserId}`,
          label: gt('Assign to me', {
            context: 'task mass-reassign picker option for the current user',
          }),
          group: usersGroup,
        },
      ]
    : [];

  const otherUsers: SingleSelectOption[] = [];
  for (const user of users) {
    if (user.id === currentUserId) continue;
    otherUsers.push({
      id: `user:${user.id}`,
      label: user.name,
      group: usersGroup,
    });
  }

  return [
    {
      id: UNASSIGNED_REASSIGN_ID,
      label: gt('Unassigned', {
        context: 'task mass-reassign picker option that clears the assignee',
      }),
    },
    ...me,
    ...otherUsers,
    ...agents.map((agent) => ({
      id: `agent:${agent.id}`,
      label: agent.name,
      group: agentsGroup,
    })),
  ];
}

/**
 * Picker options for "return to a different agent". Inactive agents are left
 * out because the server refuses to queue work for them.
 */
export function buildReturnAgentOptions(
  agents: ReadonlyArray<{ id: string; name: string; isActive: boolean }>,
): SingleSelectOption[] {
  const options: SingleSelectOption[] = [];
  for (const agent of agents) {
    if (agent.isActive) options.push({ id: agent.id, label: agent.name });
  }
  return options;
}

/** gt-react has no plural forms, so pick the source string explicitly. */
export function reassignResultMessage(
  gt: ReassignGt,
  result: { assigned: number; skipped: number },
): string {
  const context = 'toast after mass-reassigning the selected tasks';
  if (result.assigned === 0) {
    return gt('No open tasks were reassigned', { context });
  }
  if (result.skipped === 0) {
    return result.assigned === 1
      ? gt('Reassigned {assigned} task', {
          context,
          assigned: result.assigned,
        })
      : gt('Reassigned {assigned} tasks', {
          context,
          assigned: result.assigned,
        });
  }
  return result.assigned === 1
    ? gt('Reassigned {assigned} task, skipped {skipped}', {
        context,
        assigned: result.assigned,
        skipped: result.skipped,
      })
    : gt('Reassigned {assigned} tasks, skipped {skipped}', {
        context,
        assigned: result.assigned,
        skipped: result.skipped,
      });
}

/**
 * Batch-assign any number of tasks: one call per `BATCH_ASSIGN_TASKS_MAX`
 * ids, run together, with the counts summed. A full page of AR tasks (150)
 * is more than one call takes. Waits for every call to finish before
 * reporting a failure, so the dialog stays busy until nothing is in flight.
 */
export async function batchAssignInChunks(
  input: BatchAssignTasksInput,
  assign: (chunk: BatchAssignTasksInput) => Promise<BatchAssignTasksResponse>,
): Promise<BatchAssignTasksResponse> {
  const chunks: string[][] = [];
  for (let i = 0; i < input.taskIds.length; i += BATCH_ASSIGN_TASKS_MAX) {
    chunks.push(input.taskIds.slice(i, i + BATCH_ASSIGN_TASKS_MAX));
  }
  const settled = await Promise.allSettled(
    chunks.map((taskIds) => assign({ ...input, taskIds })),
  );
  const failed = settled.find((result) => result.status === 'rejected');
  if (failed) throw failed.reason;
  return settled.reduce(
    (total, result) =>
      result.status === 'fulfilled'
        ? {
            assigned: total.assigned + result.value.assigned,
            skipped: total.skipped + result.value.skipped,
          }
        : total,
    { assigned: 0, skipped: 0 },
  );
}
